/**
 * Template visual padronizado dos PDFs de relatório.
 *
 * Header: título do relatório + filtros aplicados + meta (data emissão,
 * usuário emitente).
 * Footer: numeração "X / Y" + "DTSAdmin".
 *
 * Mantido como helpers puros sem estado — `downloadAsPdf` é quem
 * orquestra a passagem do `doc` e do `meta` pro template.
 */
import type jsPDF from 'jspdf';

export interface ReportMeta {
  /** Título principal — ex.: "Espelho de Ponto". */
  title: string;
  /** Subtítulo curto — ex.: "ERICK QUEIROZ — JUNHO/2026". */
  subtitle?: string;
  /** Linhas de filtro aplicado — ex.: ["Cliente: DTS", "Status: APR"]. */
  filters?: string[];
  /** Usuário emitente (vem do `useAuth().user.nome`). */
  usuario: string;
}

const MARGIN_X = 14;
const HEADER_Y = 14;
const FOOTER_PAD = 10;

export function drawHeader(doc: jsPDF, meta: ReportMeta): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(meta.title, MARGIN_X, HEADER_Y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const emitted = new Date().toLocaleString('pt-BR');
  doc.text(`Emitido em ${emitted}`, pageWidth - MARGIN_X, HEADER_Y, { align: 'right' });
  doc.text(`Por ${meta.usuario}`, pageWidth - MARGIN_X, HEADER_Y + 4.5, { align: 'right' });

  let cursorY = HEADER_Y + 6;
  if (meta.subtitle) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    cursorY += 4.5;
    doc.text(meta.subtitle, MARGIN_X, cursorY);
  }

  if (meta.filters && meta.filters.length > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    cursorY += 5;
    doc.text(meta.filters.join(' · '), MARGIN_X, cursorY);
  }

  cursorY += 3;
  doc.setDrawColor(180);
  doc.line(MARGIN_X, cursorY, pageWidth - MARGIN_X, cursorY);
  return cursorY + 4;
}

export function drawFooter(doc: jsPDF, pageNumber: number, pageCount: number): void {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text('DTSAdmin', MARGIN_X, pageHeight - FOOTER_PAD);
  doc.text(`${pageNumber} / ${pageCount}`, pageWidth - MARGIN_X, pageHeight - FOOTER_PAD, {
    align: 'right',
  });
  doc.setTextColor(0);
}

/** Y inicial onde o conteúdo (tabela/blocos) pode começar a desenhar. */
export const CONTENT_START_Y = HEADER_Y + 22;
